import { log } from '@/services/log'
import { createTrackedItemsStore } from '../tracked-items'
import { watch, computed } from 'vue'

const createInitialFilterState = () => {
  return {
    selectedCategories: [],
    selectedLocations: [],
    selectedSites: [],
    startTimestamp: null,
    endTimestamp: null,
    period: null,
  }
}
export function useAssetsStore() {
  const storeId = 'assets'
  const storeFn = createTrackedItemsStore(
    storeId,
    {
      templateName: storeId,
      type: 'Asset',
      pageHeading: 'Assets',
      noHistory: true,
      sortingOn: 'toolbar',
      statusesFilterName: 'statuses',
      uri: '/assets',
      serviceUri: '/assets',
      listingModuleName: 'Assets',
      listingActivityName: 'View Assets',
      formModuleName: 'Assets',
      formActivityName: 'Edit Asset',
      allFields: [
        {
          key: 'propertyNumber',
          title: 'PROPERTY NUMBER',
          visible: true,
          sortable: true,
          disabled: false,
        },
        {
          key: 'name',
          title: 'NAME',
          sortable: true,
          visible: true,
          disabled: false,
        },
        {
          key: 'serialNumber',
          title: 'SERIAL NUMBER',
          visible: true,
          sortable: true,
          disabled: false,
        },
        {
          key: 'category',
          title: 'CATEGORY',
          type: 'category',
          visible: true,
          sortable: true,
          disabled: false,
        },
        {
          key: 'ownership',
          title: 'OWNERSHIP',
          visible: true,
          sortable: true,
          disabled: false,
        },
        {
          key: 'labelColor',
          title: 'LABEL COLOR',
          visible: true,
          sortable: true,
          disabled: false,
        },
        {
          key: 'locBase',
          title: 'LOCATION BASE',
          visible: false,
          sortable: true,
          disabled: false,
        },
        {
          key: 'locSub',
          title: 'LOCATION SUB',
          visible: false,
          sortable: true,
          disabled: false,
        },
        {
          key: 'contractNumber',
          title: 'CONTRACT NUMBER',
          visible: false,
          sortable: true,
          disabled: false,
        },
        {
          key: 'partNumber',
          title: 'PART NUMBER',
          visible: false,
          sortable: true,
          disabled: false,
        },
        {
          key: 'costCenter',
          title: 'COST CENTER',
          visible: false,
          sortable: true,
          disabled: false,
        },
        {
          key: 'custodian',
          title: 'CUSTODIAN',
          visible: false,
          sortable: true,
          disabled: false,
        },
        {
          key: 'manufacturer',
          title: 'MANUFACTURER',
          visible: false,
          sortable: true,
          disabled: false,
        },
        {
          key: 'model',
          title: 'MODEL',
          visible: false,
          sortable: true,
          disabled: false,
        },
        {
          key: 'expirationDate',
          title: 'EXPIRATION DATE',
          visible: true,
          sortable: true,
          disabled: false,
        },
        {
          key: 'tags',
          title: 'RFID',
          sortable: true,
          type: 'tags',
          visible: true,
          disabled: false,
        },
        {
          key: 'auditCategories',
          title: 'AUDIT CATEGORIES',
          type: 'tags',
          sortable: true,
          visible: true,
          disabled: false,
        },
        {
          key: 'lastTimestamp',
          title: 'LAST SCANNED TIME',
          type: 'micros',
          sortable: true,
          visible: true,
          disabled: false,
        },
        {
          key: 'firstTimestamp',
          title: 'FIRST SCANNED TIME',
          type: 'micros',
          sortable: true,
          visible: false,
          disabled: false,
        },
        {
          key: 'allocatedReadZone',
          title: 'ALLOCATED LOCATION',
          type: 'readZone',
          sortable: true,
          visible: false,
          disabled: false,
        },
        {
          key: 'currentReadZone',
          title: 'CURRENT LOCATION',
          type: 'readZone',
          visible: true,
          sortable: true,
          disabled: false,
        },
        {
          key: 'description',
          title: 'DESCRIPTION',
          visible: true,
          sortable: true,
          disabled: false,
        },
        {
          key: 'itemType',
          title: 'TYPE',
          visible: false,
          sortable: false,
          disabled: false,
        },
        {
          key: 'externalId',
          title: 'UUID',
          visible: false,
          sortable: false,
          disabled: false,
        },
        {
          key: 'createdBy',
          title: 'CREATED BY',
          visible: false,
          sortable: true,
          disabled: false,
        },
        {
          key: 'createdTimestamp',
          title: 'CREATED TIME',
          type: 'datetime',
          visible: false,
          sortable: true,
          disabled: false,
        },
        {
          key: 'lastModifiedBy',
          title: 'LAST MODIFIED BY',
          visible: false,
          sortable: true,
          disabled: false,
        },
        {
          key: 'lastModifiedTimestamp',
          title: 'LAST MODIFIED TIME',
          type: 'datetime',
          visible: false,
          sortable: true,
          disabled: false,
        },
        {
          key: 'status',
          title: 'STATUS',
          visible: false,
          sortable: true,
          disabled: false,
        },
        {
          key: 'actions',
          title: 'ACTIONS',
          visible: true,
          disabled: true,
          sortable: false,
        },
      ],
      apiColumnsMapping: {
        name: ['name', 'alias'],
        propertyNumber: ['property number', 'property #', 'property'],
        serialNumber: ['serialnumber', 'serial number', 'serial no', 'serial', 'serial #'],
        description: ['description', 'desc', 'info'],
        calibrationRequired: ['calibrated', 'calibrated?', 'calibration required', 'is calibration required'],
        categoryName: ['categoryname', 'category name', 'category'],
        rfid: ['tags', 'tag', 'rfid', 'rfid tag', 'rfid tags', 'tag id', 'tag ids', 'epc', 'epcs'],
        locationName: [
          'allocated readzone name',
          'allocated readzone',
          'allocated location',
          'location',
          'location name',
          'readzone',
          'read zone',
        ],
        auditCategoriesCsv: ['audit categories', 'categories'],
        status: ['status'],
        partNumber: ['part number', 'part #', 'part'],
        ownership: ['ownership', 'property type', 'ownership type'],
        labelColor: ['property type color', 'label color'],
        locBase: ['loc base', 'location base'],
        locSub: ['loc sub', 'location sub'],
        custodian: ['custodian'],
        contractNumber: ['contract', 'contract number', 'contract #'],
        costCenter: ['cost center', 'costcenter'],
        manufacturer: ['manufacturer', 'mfr'],
        model: ['model', 'model number', 'model #'],
        expirationDate: ['expiration date', 'exp. date', 'expires', 'expiration'],
      },
    },
    {
      supportedPageFilters: {
        periodApplicable: 'false',
        selectedSites: { type: 'sites' },
        selectedCategories: {
          type: 'categories',
          multiple: true,
          optionsLabel: 'name',
          submitValueKey: 'externalId',
          serviceUri: '/categories/selfAndBelow/Assets',
          dxTableName: '',
        },
        selectedLocations: {
          type: 'readzones',
          placeholder: 'Pick one or more locations to filter',
          label: 'Location(s)',
        },
      },
      filters: createInitialFilterState(),
      calibrationRequiredLabel: '',
    },
    {
      resetPageFilters() {
        const store = storeFn()
        store.filters = createInitialFilterState()
        store.supportedPageFilters.periodApplicable = 'false'
        store.resetBaseFilters()
      },
      copy(item) {
        const obj = {
          name: item.name,
          description: item.description,
          serialNumber: item.serialNumber,
          category: item.category,
          auditCategories: item.auditCategories,
          parent: item.parent,
          allocatedReadZone: item.allocatedReadZone,
          ownership: item.ownership,
          locBase: item.locBase,
          locSub: item.locSub,
          labelColor: item.labelColor,
          contractNumber: item.contractNumber,
          partNumber: item.partNumber,
          costCenter: item.costCenter,
          custodian: item.custodian,
          manufacturer: item.manufacturer,
          model: item.model,
          calibrationRequired: item.calibrationRequired,
          expirationDate: item.expirationDate,
        }
        return obj
      },
    },
    {
      fields: [
        {
          id: 'asset_category',
          name: 'category',
          label: 'Category',
          size: 6,
          component: 'CategorySingleSelect',
          componentProps: {
            multiple: false,
            placeholder: 'Pick a category',
            serviceUri: '/categories/selfAndBelow/Assets',
            optionsLabel: 'name',
            submitValueKey: 'externalId',
          },
          rules: [
            {
              required: true,
              trigger: ['blur', 'input'],
            },
          ],
        },
        {
          id: 'asset_name',
          name: 'name',
          label: 'Name',
          size: 6,
          component: 'NInput',
          componentProps: { placeholder: 'Pick a name' },
          rules: [
            {
              required: false,
              trigger: ['blur', 'input'],
            },
          ],
        },
        {
          id: 'asset_audit_categories',
          name: 'auditCategories',
          label: 'Audit Categories',
          size: 6,
          component: 'PickList',
          componentProps: { placeholder: 'Select an audit category', optionsAlias: 'AuditCategory', multiple: true },
          rules: [
            {
              required: false,
              type: 'array',
              trigger: ['blur', 'input'],
            },
          ],
        },
        {
          id: 'asset_allocated_location',
          name: 'allocatedReadZone',
          label: 'Location',
          size: 6,
          component: 'ArsSmartSelect',
          componentProps: {
            multiple: false,
            placeholder: 'Pick a location',
            dxTableName: 'Readzones',
            serviceUri: '/readzones',
            optionsLabel: 'name',
            submitValueKey: 'externalId',
          },
          rules: [
            {
              required: false,
              trigger: ['blur', 'input'],
            },
          ],
        },
        {
          id: 'asset_property_number',
          name: 'propertyNumber',
          label: 'Property Number',
          size: 4,
          component: 'NInput',
          componentProps: { placeholder: 'Enter property number' },
          rules: [
            {
              required: true,
              trigger: ['blur', 'input'],
            },
            {
              trigger: 'blur',
              remoteValidator: 'isAssetPropertyNumberUnique',
            },
          ],
        },
        {
          id: 'asset_property_type',
          name: 'ownership',
          label: 'Ownership',
          size: 4,
          component: 'NSelect',
          componentProps: { placeholder: 'Enter ownership type' },
          rules: [
            {
              required: false,
              trigger: ['blur', 'input'],
            },
          ],
        },
        {
          id: 'asset_loc_base',
          name: 'locBase',
          label: 'Location Base',
          size: 4,
          component: 'NInput',
          componentProps: { placeholder: 'Enter loc base' },
          rules: [
            {
              required: false,
              trigger: ['blur', 'input'],
            },
          ],
        },
        {
          id: 'asset_loc_sub',
          name: 'locSub',
          label: 'Location Sub',
          size: 4,
          component: 'NInput',
          componentProps: { placeholder: 'Enter loc sub' },
          rules: [
            {
              required: false,
              trigger: ['blur', 'input'],
            },
          ],
        },
        {
          id: 'asset_label_color',
          name: 'labelColor',
          label: 'Label Color',
          size: 4,
          component: 'NInput',
          componentProps: { placeholder: 'Enter label color' },
          rules: [
            {
              required: false,
              trigger: ['blur', 'input'],
            },
          ],
        },
        {
          id: 'asset_contract_number',
          name: 'contractNumber',
          label: 'Contract Number',
          size: 4,
          component: 'NInput',
          componentProps: { placeholder: 'Enter contract number' },
          rules: [
            {
              required: false,
              trigger: ['blur', 'input'],
            },
          ],
        },
        {
          id: 'asset_part_number',
          name: 'partNumber',
          label: 'Part Number',
          size: 4,
          component: 'NInput',
          componentProps: { placeholder: 'Enter part number' },
          rules: [
            {
              required: false,
              trigger: ['blur', 'input'],
            },
          ],
        },
        {
          id: 'asset_cost_center',
          name: 'costCenter',
          label: 'Cost Center',
          size: 4,
          component: 'NInput',
          componentProps: { placeholder: 'Enter cost center' },
          rules: [
            {
              required: false,
              trigger: ['blur', 'input'],
            },
          ],
        },
        {
          id: 'asset_custodian',
          name: 'custodian',
          label: 'Custodian',
          size: 4,
          component: 'NInput',
          componentProps: { placeholder: 'Enter custodian' },
          rules: [
            {
              required: false,
              trigger: ['blur', 'input'],
            },
          ],
        },
        {
          id: 'asset_manufacturer',
          name: 'manufacturer',
          label: 'Manufacturer',
          size: 4,
          component: 'NInput',
          componentProps: { placeholder: 'Enter manufacturer' },
          rules: [
            {
              required: false,
              trigger: ['blur', 'input'],
            },
          ],
        },
        {
          id: 'asset_model',
          name: 'model',
          label: 'Model',
          size: 4,
          component: 'NInput',
          componentProps: { placeholder: 'Enter model' },
          rules: [
            {
              required: false,
              trigger: ['blur', 'input'],
            },
          ],
        },
        {
          id: 'asset_serial_number',
          name: 'serialNumber',
          label: 'Serial Number',
          size: 4,
          component: 'NInput',
          componentProps: { placeholder: 'Enter serial number' },
          rules: [
            {
              required: false,
              trigger: ['blur', 'input'],
            },
          ],
        },
        {
          id: 'asset_calibration_required',
          name: 'calibrationRequired',
          size: 4,
          component: 'NCheckbox',
          label: 'Calibration Required',
          templateLabel: 'calibrationRequiredLabel',
          defaultValue: false,
          componentProps: {},
          rules: [
            {
              required: false,
              type: 'boolean',
              trigger: ['blur', 'input'],
            },
          ],
        },
        {
          id: 'asset_expiration_date',
          name: 'expirationDate',
          size: 4,
          component: 'ArsDatePicker',
          label: 'Expiration Date',
          componentProps: {},
          rules: [
            {
              required: false,
              type: 'date',
              trigger: ['blur', 'input'],
            },
          ],
        },
        {
          id: 'asset_tags',
          name: 'tags',
          label: 'RFID',
          size: 8,
          component: 'ArsTagSelect',
          componentProps: {
            multiple: true,
            filterable: true,
            placeholder: 'Add a tag',
          },
          rules: [
            {
              required: false,
              type: 'array',
              customValidator: 'isValidTag',
              trigger: 'blur',
            },
            {
              trigger: 'blur',
              remoteValidator: 'isRfidUnique',
            },
          ],
        },
        {
          id: 'asset_description',
          name: 'description',
          label: 'Description',
          size: 16,
          component: 'NInput',
          componentProps: { placeholder: 'Enter a description' },
          rules: [
            {
              required: false,
              trigger: ['blur', 'input'],
            },
          ],
        },
      ],
    }
  )
  const store = storeFn()
  const calibrationRequired = computed(() => store?.formData?.calibrationRequired ?? true)
  watch(
    calibrationRequired,
    (checked) => {
      store.form.calibrationRequiredLabel = checked ? 'Calibration Required' : 'Calibration Not Required'
    },
    { immediate: true }
  )
  return store
}
